import { Box, Flex, Stat, StatHelpText, StatLabel, StatNumber, StatProps } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { Icon } from '../Icon'
import { SECTION_CARD_SHELL } from './SectionCard'

/**
 * Fundo do ladrilho do ícone: o tom 50 da mesma escala do acento. Só funciona para tokens `.500`,
 * que é o que todos os acentos do painel são (`magenta.500`, `abraGreen.500`, `blue.500`…) — as
 * cinco cópias do card já faziam exatamente esta conta.
 */
const accentSurface = (color: string) => color.replace('.500', '.50')

export interface IStatCardProps extends Omit<StatProps, 'color' | 'title'> {
  /** Rótulo do indicador, em pt-BR. Vai para caixa alta pelo próprio componente. */
  label: ReactNode
  /** O número. Já formatado — moeda por `formatCurrency`, contagem por `formatNumber`. */
  value: ReactNode
  /** Linha de apoio abaixo do número. */
  helpText?: ReactNode
  /** Token de cor do acento (`magenta.500`). Pinta a barra da esquerda e o ladrilho do ícone. */
  color: string
  /** Classe do LineIcons (`lni lni-users`) ou um nó — vai dentro do ladrilho de 36px. */
  icon?: ReactNode
  /** Substitui o ladrilho inteiro, para o card que traz um gráfico no lugar do ícone. */
  aside?: ReactNode
  /** Pinta o número com a cor do acento, para o card que é o destaque da linha. */
  highlight?: boolean
  /** Nó ao lado do número — o botão de editar valor do card de receita. */
  action?: ReactNode
}

/**
 * Card de indicador: a mesma casca do `<SectionCard>` com uma barra de acento na esquerda, o
 * rótulo em caixa alta, o número e o ladrilho do ícone.
 *
 * Estava escrito cinco vezes — `EventDetailPage.StatCard`, `EventDetailPage.FreeTicketsStatCard`,
 * `EventAdminPage.StatCard`, `ContestDetailPage.StatMiniCard` e `components/revenueSummary/
 * RevenueCard` — com ~45 linhas idênticas, inclusive a conta do `color.replace('.500', '.50')`.
 * As cópias já tinham divergido: o número era `2xl` em três e `xl` com `fontFamily="mono"` numa,
 * a linha de apoio `xs` em três e `10px` numa, e a do `ContestDetailPage` usava `<Text>` no lugar
 * de `<StatHelpText>` — o que a tirava do `<dl>` do `<Stat>` e desmanchava o par rótulo/valor
 * que é a razão de o componente existir.
 */
export default function StatCard({
  label,
  value,
  helpText,
  color,
  icon,
  aside,
  highlight = false,
  action,
  ...rest
}: IStatCardProps) {
  return (
    <Stat {...SECTION_CARD_SHELL} px={5} py={4} borderLeft="4px solid" borderLeftColor={color} {...rest}>
      <Flex justify="space-between" align="start" gap={3}>
        <Box minW={0}>
          <StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">
            {label}
          </StatLabel>
          <Flex align="center" gap={1} mt={1}>
            <StatNumber fontSize="2xl" fontWeight="800" color={highlight ? color : 'gray.800'}>
              {value}
            </StatNumber>
            {action}
          </Flex>
          {helpText != null && (
            <StatHelpText fontSize="xs" color="gray.400" mb={0} mt={1}>
              {helpText}
            </StatHelpText>
          )}
        </Box>
        {aside ??
          (icon != null && (
            <Flex
              w="36px"
              h="36px"
              borderRadius="lg"
              align="center"
              justify="center"
              bg={accentSurface(color)}
              color={color}
              flexShrink={0}
              mt={1}
            >
              {typeof icon === 'string' ? <Icon name={icon} size="lg" /> : icon}
            </Flex>
          ))}
      </Flex>
    </Stat>
  )
}
